import { api } from '../services/api';

export interface DocumentConfig {
  contractType: string;
  apiEndpoint: string;
  displayName: string;
}

export const DocumentConfigs: Record<string, DocumentConfig> = {
  purchase: {
    contractType: 'purchase',
    apiEndpoint: 'purchase-contract',
    displayName: 'Kupní smlouva'
  },
  rental: {
    contractType: 'rental', 
    apiEndpoint: 'rental-contract',
    displayName: 'Nájemní smlouva'
  },
  employment: {
    contractType: 'employment',
    apiEndpoint: 'employment-contract', 
    displayName: 'Pracovní smlouva'
  },
  work: {
    contractType: 'work',
    apiEndpoint: 'work-contract',
    displayName: 'Smlouva o dílo'
  },
  gift: {
    contractType: 'gift',
    apiEndpoint: 'gift-contract',
    displayName: 'Darovací smlouva'
  }
};

export class DocumentManager {
  
  /**
   * Univerzální stahování PDF pro všechny typy dokumentů
   */
  static async downloadDocument(
    contractData: any,
    setDownloading: (loading: boolean) => void,
    setSuccessMessage: (message: string) => void,
    modifiedContent?: string
  ): Promise<void> {
    if (!contractData) {
      console.error('No contract data available for download');
      return;
    }

    setDownloading(true);

    try {
      const config = DocumentConfigs[contractData.contract_type];
      if (!config) {
        throw new Error(`Unknown contract type: ${contractData.contract_type}`);
      }

      // Prefer uložit upravený obsah (pokud je) – zajistí PDF i bez kompletních polí formuláře
      let downloadResponse: any = null;

      const trySaveEdited = async (content: string | undefined) => {
        if (!content) return null;
        try {
          return await api.post('/documents/save-edited', {
            filename: contractData.filename || `${config.displayName.toLowerCase().replace(' ', '_')}_${Date.now()}.pdf`,
            modified_content: content,
            contract_type: contractData.contract_type || config.contractType,
          });
        } catch (_e: any) {
          return null; // např. 401 (bez přihlášení) – ignorovat, zkusíme jiné cesty
        }
      };

      const tryGenerate = async () => {
        try {
          return await api.post(`/documents/generate/${config.apiEndpoint}`, contractData.form_data);
        } catch (e: any) {
          if (e?.response?.status === 422) {
            // validace selhala – zkus test endpoint
            try {
              return await api.post(`/documents/test/generate/${config.apiEndpoint}`, contractData.form_data);
            } catch (_e2: any) {
              return null;
            }
          }
          return null;
        }
      };

      // 1) Pokud máme upravený obsah, zkus save-edited jako první
      if (modifiedContent) {
        downloadResponse = await trySaveEdited(modifiedContent);
      }

      // 2) Pokud nic, zkus standardní generování (auth → test)
      if (!downloadResponse) {
        downloadResponse = await tryGenerate();
      }

      // 3) Pokud pořád nic, ale máme preview text, zkus save‑edited s náhledem
      if (!downloadResponse) {
        downloadResponse = await trySaveEdited(contractData.text_content);
      }

      // Notify dashboard/recent activity listeners
      try {
        if (downloadResponse?.data) {
          const detail = {
            ...downloadResponse.data,
            contract_type: contractData.contract_type,
            form_data: contractData.form_data
          };
          window.dispatchEvent(new CustomEvent('document-generated', { detail }));
        }
      } catch (_) { /* ignore */ }

      if (downloadResponse?.data?.download_url || downloadResponse?.data?.download_url_pdf) {
        const downloadUrl = downloadResponse.data.download_url_pdf || downloadResponse.data.download_url;

        try {
          // Clean up the download URL - remove /api/v1/ prefix if present since api service adds it
          const prefix = '/api/v1/';
          const cleanDownloadUrl = downloadUrl.startsWith(prefix)
            ? downloadUrl.substring(prefix.length)
            : downloadUrl.replace(/^\//, '');
          
          // Use API service which handles CORS properly
          const response = await api.get(cleanDownloadUrl, {
            responseType: 'blob'
          });
          
          // Create blob and download
          const blob = new Blob([response.data], { type: 'application/pdf' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = downloadResponse.data.filename || `${config.displayName.toLowerCase().replace(' ', '_')}.pdf`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          
          setSuccessMessage(`${config.displayName} byla úspěšně stažena!`);
        } catch (downloadError: any) {
          console.error('Download error:', downloadError);
          // Fallback: try to open in new window
          const fullUrl = downloadUrl;
          window.open(fullUrl, '_blank');
          setSuccessMessage('PDF se otevře v novém okně - můžete ho tam stáhnout');
        }
      } else {
        throw new Error('No download URL received from server');
      }
      
    } catch (error: any) {
      console.error('Download error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Neznámá chyba';
      setSuccessMessage(`Chyba při stahování ${DocumentConfigs[contractData.contract_type]?.displayName || 'dokumentu'}: ${errorMessage}`);
    } finally {
      setDownloading(false);
    }
  }

  /**
   * Stáhne DOCX verzi (vygeneruje ji on‑demand, pokud chybí)
   */
  static async downloadDocumentDocx(
    contractData: any,
    setDownloading: (loading: boolean) => void,
    setSuccessMessage: (message: string) => void,
    modifiedContent?: string
  ): Promise<void> {
    if (!contractData) {
      console.error('No contract data available for DOCX download');
      return;
    }

    setDownloading(true);
    try {
      const config = DocumentConfigs[contractData.contract_type];
      if (!config) {
        throw new Error(`Unknown contract type: ${contractData.contract_type}`);
      }

      // 1) Zkusit uložit upravený obsah (pokud je), aby existoval PDF základ
      let generateResponse: any = null;
      const trySaveEdited = async (content: string | undefined) => {
        if (!content) return null;
        try {
          return await api.post('/documents/save-edited', {
            filename: contractData.filename || `${config.displayName.toLowerCase().replace(' ', '_')}_${Date.now()}.pdf`,
            modified_content: content,
            contract_type: contractData.contract_type || config.contractType,
          });
        } catch (_e: any) {
          return null;
        }
      };

      const tryGenerate = async () => {
        try {
          return await api.post(`/documents/generate/${config.apiEndpoint}`, contractData.form_data);
        } catch (e: any) {
          if (e?.response?.status === 422) {
            try {
              return await api.post(`/documents/test/generate/${config.apiEndpoint}`, contractData.form_data);
            } catch (_e2: any) {
              return null;
            }
          }
          return null;
        }
      };

      // save‑edited (pokud máme upravený obsah), jinak standardní generování
      if (modifiedContent) {
        generateResponse = await trySaveEdited(modifiedContent);
      }
      if (!generateResponse) {
        generateResponse = await tryGenerate();
      }
      if (!generateResponse) {
        generateResponse = await trySaveEdited(contractData.text_content);
      }

      const docxUrl: string | undefined = generateResponse?.data?.download_url_docx;

      // 2) Pokud URL není, pokus o on‑demand DOCX přes download‑docx/{base}
      if (!docxUrl) {
        const filename: string = (generateResponse?.data?.filename || contractData?.filename || '').toString();
        const base = filename.toLowerCase().endsWith('.pdf') ? filename.slice(0, -4) : filename;

        // nejprve autentizovaná cesta
        try {
          const resp = await api.get(`/documents/download-docx/${base}`, { responseType: 'blob' });
          const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${base}.docx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          setSuccessMessage(`${config.displayName} (DOCX) byla úspěšně stažena!`);
          return;
        } catch (_) {
          // fallback test endpoint
          const resp = await api.get(`/documents/test/download-docx/${base}`, { responseType: 'blob' });
          const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.download = `${base}.docx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          setSuccessMessage(`${config.displayName} (DOCX) byla úspěšně stažena!`);
          return;
        }
      }

      // 3) Pokud máme přímo URL z generování, stáhneme přes axios (zachování CORS)
      if (docxUrl) {
        const cleanPath = docxUrl.startsWith('/api/v1/') ? docxUrl.substring(7) : docxUrl.replace(/^\//, '');
        try {
          const resp = await api.get(cleanPath, { responseType: 'blob' });
          const blob = new Blob([resp.data], { type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' });
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          const base = (generateResponse?.data?.filename || contractData?.filename || 'dokument').replace(/\.pdf$/i, '');
          link.download = `${base}.docx`;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          window.URL.revokeObjectURL(url);
          setSuccessMessage(`${config.displayName} (DOCX) byla úspěšně stažena!`);
          return;
        } catch (e) {
          // Fallback otevření do nového okna pokud blob selže
          window.open(docxUrl, '_blank');
          setSuccessMessage('DOCX se otevře v novém okně - můžete ho tam stáhnout');
          return;
        }
      }

      throw new Error('Nepodařilo se stáhnout DOCX');
    } catch (error: any) {
      console.error('Download DOCX error:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Neznámá chyba';
      setSuccessMessage(`Chyba při stahování DOCX: ${errorMessage}`);
    } finally {
      setDownloading(false);
    }
  }

  /**
   * Univerzální preview handling
   */
  static transformPreviewData(result: any, contractType: string): any {
    const config = DocumentConfigs[contractType];
    if (!config) {
      throw new Error(`Unknown contract type: ${contractType}`);
    }

    return {
      text_content: result.preview_text || '',
      filename: `${config.displayName.toLowerCase().replace(' ', '_')}_${Date.now()}.pdf`,
      download_url: '/api/v1/documents/generate',
      generated_at: new Date().toISOString(),
      contract_type: contractType,
      form_data: result.form_data
    };
  }

  /**
   * Univerzální success handling 
   */
  static transformSuccessData(result: any, contractType: string): any {
    const config = DocumentConfigs[contractType];
    if (!config) {
      throw new Error(`Unknown contract type: ${contractType}`);
    }

    return {
      ...result,
      contract_type: contractType
    };
  }

  /**
   * Get display name for contract type
   */
  static getDisplayName(contractType: string): string {
    return DocumentConfigs[contractType]?.displayName || 'Dokument';
  }

  /**
   * Get API endpoint for contract type  
   */
  static getApiEndpoint(contractType: string): string {
    const config = DocumentConfigs[contractType];
    if (!config) {
      throw new Error(`Unknown contract type: ${contractType}`);
    }
    return config.apiEndpoint;
  }
}
